import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../services/api';
import { dynamicConfigApi } from '../../services/dynamicConfigApi';
import { AdminScreenKey } from '../../types/admin-roles';
import { DynamicConfigChangeRequest, DynamicConfigEntry } from '../../types/dynamicConfig';
import { useToast } from '../promotions/useToast';
import { actorLabel, formatWhen } from './ConfigKeyEditor';

export type CardLoad =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  /** The list answered without one of the card's keys: the caller lacks it or the build predates it. */
  | { kind: 'missing'; keys: string[] }
  | { kind: 'ready' };

export const SAVED_MESSAGE = 'Saved — live within a minute';
export const FORBIDDEN_MESSAGE = 'You no longer have permission to change these settings.';

/** Equal as the key's type compares them: numbers by value ("5" = "5.00"), text trimmed. */
export function sameValue(entry: Pick<DynamicConfigEntry, 'type'> | undefined, a: string, b: string): boolean {
  if (entry?.type === 'NUMBER') {
    const x = Number(a.trim());
    const y = Number(b.trim());
    if (a.trim() !== '' && b.trim() !== '' && Number.isFinite(x) && Number.isFinite(y)) return x === y;
  }
  return a.trim() === b.trim();
}

/**
 * #1113 — the shared state of a Finance config card (design DD10): loads the Runtime Config list,
 * picks the card's keys, holds the draft, diffs it against the server copy, saves the changed keys
 * in one atomic `PATCH`, maps a 400/403 `key` onto its field, and re-reads after a save.
 *
 * `defaults` are the deployed values for keys with no override (the API deliberately does not report
 * them; `financeConfigDefaults.ts` pins them to the services' code). A key the list does not return is
 * an error, never a default: the tab is only shown to holders of the card's screen, so a missing key
 * means a stale grant or an older platform build.
 */
export function useConfigCard({
  keys,
  screen,
  defaults,
  token,
  watch = [],
}: {
  keys: readonly string[];
  /** The screen every key of this card must declare (`adminScreen`); a mismatch is a catalog drift. */
  screen: AdminScreenKey;
  defaults: Record<string, string>;
  token: string;
  /**
   * Spec 085 — keys the card only shows, read from the same list when the caller may see them (e.g.
   * a kill switch on `dynamic-config`). Never edited, never "missing", not in the draft.
   */
  watch?: readonly string[];
}) {
  const [load, setLoad] = useState<CardLoad>({ kind: 'loading' });
  const [entries, setEntries] = useState<Map<string, DynamicConfigEntry>>(new Map());
  const [watched, setWatched] = useState<Map<string, DynamicConfigEntry>>(new Map());
  const [draft, setDraftState] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const { toast, show } = useToast();
  const keyList = keys.join('\n');
  const watchList = watch.join('\n');

  const serverOf = useCallback((map: Map<string, DynamicConfigEntry>) => {
    const out: Record<string, string> = {};
    for (const k of keyList.split('\n')) {
      const e = map.get(k);
      out[k] = e?.value ?? defaults[k] ?? '';
    }
    return out;
    // defaults is a module constant per card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyList]);

  /** Loads the list; `silent` keeps the card on screen while it re-reads (after a save). */
  const reload = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setLoad({ kind: 'loading' });
    setForbidden(false);
    setFormError(null);
    setFieldErrors({});
    try {
      const all = await dynamicConfigApi.list(token);
      const wanted = keyList.split('\n');
      const map = new Map<string, DynamicConfigEntry>();
      for (const e of all ?? []) if (wanted.includes(e.key)) map.set(e.key, e);
      const seen = new Map<string, DynamicConfigEntry>();
      if (watchList) for (const e of all ?? []) if (watchList.split('\n').includes(e.key)) seen.set(e.key, e);
      const missing = wanted.filter((k) => !map.has(k));
      if (import.meta.env.DEV) {
        const drift = [...map.values()].filter((e) => e.adminScreen && e.adminScreen !== screen);
        if (drift.length) console.warn(`[#1113] keys not on the ${screen} screen:`, drift.map((e) => e.key));
      }
      setEntries(map);
      setWatched(seen);
      setDraftState(serverOf(map));
      setLoad(missing.length ? { kind: 'missing', keys: missing } : { kind: 'ready' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        setLoad({ kind: 'error', message: FORBIDDEN_MESSAGE });
      } else {
        setLoad({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load these settings' });
      }
    }
  }, [token, keyList, watchList, screen, serverOf]);

  useEffect(() => { void reload(); }, [reload]);

  const server = useMemo(() => serverOf(entries), [entries, serverOf]);

  const setField = useCallback((key: string, value: string) => {
    setDraftState((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setFormError(null);
  }, []);

  const setFields = useCallback((values: Record<string, string>) => {
    setDraftState((prev) => ({ ...prev, ...values }));
    setFormError(null);
  }, []);

  const discard = useCallback(() => {
    setDraftState(server);
    setFieldErrors({});
    setFormError(null);
  }, [server]);

  /** The keys of `values` whose value differs from the server copy, as a batch. */
  const changesFrom = useCallback((values: Record<string, string>): DynamicConfigChangeRequest[] =>
    Object.entries(values)
      .filter(([k, v]) => !sameValue(entries.get(k), v, server[k] ?? ''))
      .map(([key, value]) => ({ key, value: value.trim() })),
  [entries, server]);

  /**
   * Writes `changes` in one atomic request, then reverts `clears` (a value the key's pattern cannot
   * hold, e.g. an empty pilot list, is a revert). Re-reads the list on success. Returns true on success.
   * When the batch applied but a revert failed, the card re-reads (the batch is live) and shows
   * `clearFailedMessage` instead of "Nothing was saved".
   */
  const save = useCallback(async (
    changes: DynamicConfigChangeRequest[],
    clears: string[] = [],
    opts: { clearFailedMessage?: string } = {},
  ) => {
    if (!changes.length && !clears.length) return true;
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    let batchApplied = false;
    try {
      if (changes.length) {
        await dynamicConfigApi.setMany(changes, token);
        batchApplied = true;
      }
      try {
        for (const k of clears) await dynamicConfigApi.clear(k, token);
      } catch (err) {
        if (!batchApplied) throw err;
        // The batch is live; only the revert failed. Show the server copy and say exactly that.
        await reload({ silent: true });
        setFormError(`${opts.clearFailedMessage ?? 'The other changes were saved, but a setting could not be reverted.'} ${
          err instanceof Error ? err.message : ''}`.trim());
        return false;
      }
      await reload({ silent: true });
      show(SAVED_MESSAGE);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        setFormError(FORBIDDEN_MESSAGE);
      } else if (err instanceof ApiError && err.status === 400 && err.key && keyList.split('\n').includes(err.key)) {
        setFieldErrors({ [err.key]: err.serverMessage ?? err.message });
        setFormError('Nothing was saved. Fix the field marked below.');
      } else {
        setFormError(`Nothing was saved. ${err instanceof Error ? err.message : 'The save failed.'}`);
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [token, reload, show, keyList]);

  /** "Last changed by X, date" across the card's keys, or "Never changed here". */
  const lastChanged = useMemo(() => {
    const latest = [...entries.values()]
      .filter((e) => e.overridden && e.updatedAt)
      .sort((a, b) => (b.updatedAt as string).localeCompare(a.updatedAt as string))[0];
    if (!latest) return 'Never changed here';
    const who = actorLabel(latest.updatedBy, latest.updatedByName);
    return `Last changed${who ? ` by ${who}` : ''}, ${formatWhen(latest.updatedAt)}`;
  }, [entries]);

  return {
    load, entries, watched, server, draft, setField, setFields, discard, changesFrom, save, saving,
    fieldErrors, formError, forbidden, reload, lastChanged, toast,
  };
}
